/**
 * Uploads-Panel — Dashboard-Overlay für Bild-Uploads vom Handy.
 *
 *   - Capture-Button (input type=file capture=environment) → Kamera auf iOS
 *   - Live-Liste aller Uploads mit Status + Prozent-Bar
 *   - Polling /upload/:id/progress alle 500ms während aktiv
 *   - Thumbnail wird nach `done`-Status nachgeladen (blob via Bearer-fetch,
 *     damit Token nicht in URL leakt)
 */

import {
  uploadImage,
  fetchUploadProgress,
  listUploads,
  deleteUpload,
  fetchThumbBlobUrl,
  type UploadProgress,
} from "./api";

const ALLOWED = new Set(["image/jpeg", "image/png", "image/webp"]);
const MAX_BYTES = 32 * 1024 * 1024;

interface RowState {
  job: UploadProgress;
  thumbBlobUrl?: string;
  pollTimer?: ReturnType<typeof setInterval>;
}

export class UploadsPanel {
  private host: HTMLElement;
  private listEl!: HTMLUListElement;
  private emptyEl!: HTMLDivElement;
  private state = new Map<string, RowState>();
  private onToast: (msg: string) => void;

  constructor(host: HTMLElement, onToast: (msg: string) => void) {
    this.host = host;
    this.onToast = onToast;
    this.render();
  }

  private render(): void {
    this.host.innerHTML = `
      <div class="uploads-card glass">
        <header class="uploads-head">
          <div>
            <h3 class="uploads-title">Bild-Uploads</h3>
            <p class="uploads-sub">Vom Handy: Foto wählen oder direkt mit der Kamera aufnehmen.</p>
          </div>
          <button type="button" class="uploads-close icon-btn ghost" id="uploadsClose" aria-label="Schließen">✕</button>
        </header>
        <div class="uploads-actions">
          <label class="uploads-action-btn">
            <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" id="uploadsCapture" hidden />
            <span class="uploads-action-icon" aria-hidden="true">📷</span>
            <span>Foto aufnehmen</span>
          </label>
          <label class="uploads-action-btn">
            <input type="file" accept="image/jpeg,image/png,image/webp" multiple id="uploadsPick" hidden />
            <span class="uploads-action-icon" aria-hidden="true">🖼️</span>
            <span>Aus Galerie wählen</span>
          </label>
        </div>
        <ul class="uploads-list" id="uploadsList" aria-live="polite"></ul>
        <div class="uploads-empty" id="uploadsEmpty">
          <p>Noch keine Uploads.</p>
          <p class="uploads-empty-hint">JPEG · PNG · WebP — max 32 MB. Thumbnails werden serverseitig generiert.</p>
        </div>
      </div>
    `;
    this.listEl = this.host.querySelector("#uploadsList") as HTMLUListElement;
    this.emptyEl = this.host.querySelector("#uploadsEmpty") as HTMLDivElement;

    const capture = this.host.querySelector("#uploadsCapture") as HTMLInputElement;
    const pick = this.host.querySelector("#uploadsPick") as HTMLInputElement;
    const close = this.host.querySelector("#uploadsClose") as HTMLButtonElement;

    capture.addEventListener("change", () => {
      const files = capture.files ? Array.from(capture.files) : [];
      capture.value = "";
      for (const f of files) this.startUpload(f);
    });
    pick.addEventListener("change", () => {
      const files = pick.files ? Array.from(pick.files) : [];
      pick.value = "";
      for (const f of files) this.startUpload(f);
    });
    close.addEventListener("click", () => this.hide());
  }

  show(): void {
    this.host.classList.add("is-open");
    this.host.hidden = false;
    void this.refresh();
  }

  hide(): void {
    this.host.classList.remove("is-open");
    this.host.hidden = true;
    // Polling für offene Jobs läuft weiter im Hintergrund, damit auch beim
    // wieder-öffnen die Liste aktuell ist.
  }

  async refresh(): Promise<void> {
    try {
      const items = await listUploads();
      // Bestehende Polling-Timer beibehalten — wir mergen nur neue/aktualisierte Jobs.
      const seen = new Set<string>();
      for (const job of items) {
        seen.add(job.id);
        this.upsert(job);
      }
      // Jobs die nicht mehr im Server-Index sind, lokal entfernen.
      for (const id of [...this.state.keys()]) {
        if (!seen.has(id)) this.removeRow(id);
      }
      this.refreshEmptyState();
    } catch (err) {
      console.warn("[uploads] refresh failed", err);
    }
  }

  private async startUpload(file: File): Promise<void> {
    if (!ALLOWED.has(file.type)) {
      this.onToast("Nur JPEG, PNG oder WebP");
      return;
    }
    if (file.size > MAX_BYTES) {
      this.onToast("Datei zu groß (max 32 MB)");
      return;
    }
    // Optimistic row — wird sofort mit Pending angezeigt, IDs ersetzen wir nach
    // dem Server-OK durch die echte upload-id.
    const localId = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const placeholder: UploadProgress = {
      id: localId,
      status: "uploading",
      progress: 0,
      bytes_received: 0,
      size: file.size,
      mime: file.type,
      filename: file.name,
      thumbAvailable: false,
      createdAt: new Date().toISOString(),
      finishedAt: null,
      error: null,
      fileUrl: "",
      thumbUrl: "",
      progressUrl: "",
    };
    this.upsert(placeholder);
    try {
      const res = await uploadImage(file, file.name, (ev) => {
        const row = this.state.get(localId);
        if (!row) return;
        row.job.bytes_received = ev.loaded;
        row.job.size = ev.total;
        row.job.progress = ev.percent;
        this.renderRow(row);
      });
      // Server-Job ist da — local-row gegen echte ID austauschen + Polling starten.
      this.removeRow(localId);
      const initial: UploadProgress = {
        ...placeholder,
        id: res.id,
        status: "processing",
        progress: 100,
        fileUrl: res.fileUrl,
        thumbUrl: res.thumbUrl,
        progressUrl: res.progressUrl,
      };
      this.upsert(initial);
      this.startPolling(res.id);
    } catch (err) {
      const row = this.state.get(localId);
      if (row) {
        row.job.status = "failed";
        row.job.error = err instanceof Error ? err.message : String(err);
        this.renderRow(row);
      }
      this.onToast(err instanceof Error ? err.message : "Upload fehlgeschlagen");
    }
  }

  private startPolling(id: string): void {
    const row = this.state.get(id);
    if (!row || row.pollTimer) return;
    const tick = async () => {
      try {
        const next = await fetchUploadProgress(id);
        const rowNow = this.state.get(id);
        if (!rowNow) return;
        rowNow.job = { ...rowNow.job, ...next };
        this.renderRow(rowNow);
        if (next.status === "done") {
          if (next.thumbAvailable && !rowNow.thumbBlobUrl) {
            try {
              rowNow.thumbBlobUrl = await fetchThumbBlobUrl(id);
              this.renderRow(rowNow);
            } catch {
              // ignore — Thumb darf fehlen
            }
          }
          this.stopPolling(id);
        } else if (next.status === "failed") {
          this.stopPolling(id);
        }
      } catch (err) {
        // Polling-Fehler nicht laut schreien; einmaliger Hicks verträglich.
        console.warn("[uploads] poll", err);
      }
    };
    void tick();
    row.pollTimer = setInterval(tick, 500);
  }

  private stopPolling(id: string): void {
    const row = this.state.get(id);
    if (!row || !row.pollTimer) return;
    clearInterval(row.pollTimer);
    row.pollTimer = undefined;
  }

  private upsert(job: UploadProgress): void {
    const existing = this.state.get(job.id);
    if (existing) {
      existing.job = { ...existing.job, ...job };
      this.renderRow(existing);
    } else {
      const li = document.createElement("li");
      li.className = "upload-row";
      li.dataset.id = job.id;
      this.listEl.prepend(li);
      const row: RowState = { job };
      this.state.set(job.id, row);
      this.renderRow(row);
      // Existierende, noch nicht fertige Server-Jobs (z.B. via refresh()) polling starten.
      if ((job.status === "uploading" || job.status === "processing" || job.status === "pending") && !job.id.startsWith("local-")) {
        this.startPolling(job.id);
      }
      // Wenn fertig + Thumb verfügbar: gleich Blob laden.
      if (job.status === "done" && job.thumbAvailable && !row.thumbBlobUrl) {
        fetchThumbBlobUrl(job.id)
          .then((url) => {
            row.thumbBlobUrl = url;
            this.renderRow(row);
          })
          .catch(() => {
            // ignore
          });
      }
    }
    this.refreshEmptyState();
  }

  private removeRow(id: string): void {
    const row = this.state.get(id);
    if (!row) return;
    this.stopPolling(id);
    if (row.thumbBlobUrl) URL.revokeObjectURL(row.thumbBlobUrl);
    this.state.delete(id);
    const li = this.listEl.querySelector(`li.upload-row[data-id="${cssEscape(id)}"]`);
    li?.remove();
    this.refreshEmptyState();
  }

  private renderRow(row: RowState): void {
    const li = this.listEl.querySelector(
      `li.upload-row[data-id="${cssEscape(row.job.id)}"]`,
    ) as HTMLLIElement | null;
    if (!li) return;
    const { job, thumbBlobUrl } = row;
    const statusLabel: Record<UploadProgress["status"], string> = {
      pending: "Wartet",
      uploading: "Upload",
      processing: "Verarbeitet",
      done: "Fertig",
      failed: "Fehler",
    };
    const percent = Math.max(0, Math.min(100, job.progress || 0));
    const sizeKb = job.size ? `${Math.round(job.size / 1024)} KB` : "?";
    const sentKb = job.bytes_received ? `${Math.round(job.bytes_received / 1024)} KB` : "0 KB";
    const isLocal = job.id.startsWith("local-");
    const thumbHtml = thumbBlobUrl
      ? `<img class="upload-thumb-img" src="${thumbBlobUrl}" alt="" />`
      : `<div class="upload-thumb-placeholder" aria-hidden="true">${job.status === "done" ? "✓" : "…"}</div>`;
    li.classList.remove("is-pending", "is-uploading", "is-processing", "is-done", "is-failed");
    li.classList.add(`is-${job.status}`);
    li.innerHTML = `
      <a class="upload-thumb" href="${isLocal ? "#" : job.fileUrl}" target="_blank" rel="noopener" aria-label="Datei öffnen">
        ${thumbHtml}
      </a>
      <div class="upload-meta">
        <div class="upload-meta-row">
          <span class="upload-filename" title="${escapeHtml(job.filename)}">${escapeHtml(job.filename)}</span>
          <span class="upload-status upload-status-${job.status}">${statusLabel[job.status]}</span>
        </div>
        <div class="upload-progress-track">
          <div class="upload-progress-fill" style="width:${percent}%"></div>
        </div>
        <div class="upload-meta-row upload-meta-row-secondary">
          <span class="upload-bytes">${sentKb} / ${sizeKb} · ${percent}%</span>
          ${job.error ? `<span class="upload-error" title="${escapeHtml(job.error)}">${escapeHtml(job.error)}</span>` : ""}
          ${
            isLocal
              ? ""
              : `<button type="button" class="upload-delete ghost-btn" data-act="delete">Entfernen</button>`
          }
        </div>
      </div>
    `;
    const del = li.querySelector('[data-act="delete"]') as HTMLButtonElement | null;
    if (del) {
      del.addEventListener("click", async (e) => {
        e.preventDefault();
        del.disabled = true;
        try {
          await deleteUpload(job.id);
          this.removeRow(job.id);
        } catch (err) {
          del.disabled = false;
          this.onToast(err instanceof Error ? err.message : "Löschen fehlgeschlagen");
        }
      });
    }
  }

  private refreshEmptyState(): void {
    this.emptyEl.hidden = this.state.size > 0;
  }
}

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function cssEscape(value: string): string {
  if (typeof (window as any).CSS?.escape === "function") return (window as any).CSS.escape(value);
  return value.replace(/[^a-zA-Z0-9_-]/g, "_");
}
